
import { useState, useEffect, useRef } from "react";
import { BookOpen } from "lucide-react";
import { imageService } from "@/services/image-service";
import { OptimizedImage } from "@/components/OptimizedImage";
import scifiPlaceholder from "@/assets/book-placeholder-scifi.jpg";
import classicPlaceholder from "@/assets/book-placeholder-classic.jpg";
import generalPlaceholder from "@/assets/book-placeholder-general.jpg";
import { appleArtwork, coverAtSize } from "../../supabase/functions/_shared/bookCover";

interface EnhancedBookCoverProps {
  title: string;
  author?: string;
  isbn?: string;
  coverUrl?: string;
  thumbnailUrl?: string;
  smallThumbnailUrl?: string;
  className?: string;
  lazy?: boolean;
  /**
   * Fires with the URL this component actually settled on. It resolves covers
   * itself — cache, Google, Open Library, Archive — so a card can be SHOWING
   * art that the `coverUrl` prop never contained. Consumers that need the real
   * URL (the enlarge control) must ask for it rather than read the prop.
   */
  onCoverResolved?: (url: string) => void;
}


/**
 * How long the cover recovery chain gets before we give up and show the
 * placeholder. The lookups it awaits (Supabase cache, Open Library, an
 * Archive.org edge function) have no internal timeout, so without this a slow
 * or dead upstream leaves a spinner on screen forever. Generous enough that a
 * merely slow lookup still wins.
 */
const COVER_RECOVERY_TIMEOUT_MS = 8000;

// The Open Library title-search timeout and circuit breaker were removed with
// the title search itself (2026-09-27): the live lookup is now a single
// ISBN image load, which imageService already bounds with its own timeout.

const EnhancedBookCover = ({ 
  title, 
  author,
  isbn,
  coverUrl, 
  thumbnailUrl, 
  smallThumbnailUrl, 
  className = "w-12 h-16",
  lazy = false, // Default to false for immediate loading
  onCoverResolved
}: EnhancedBookCoverProps) => {
  const [currentSrc, setCurrentSrc] = useState<string | null>(null);

  // Report the settled URL from ONE place. There are ten setCurrentSrc call
  // sites across the fallback chain; calling back at each would eventually miss
  // one. Held in a ref so an inline arrow from the parent doesn't re-fire this
  // on every render.
  const onResolvedRef = useRef(onCoverResolved);
  onResolvedRef.current = onCoverResolved;
  useEffect(() => {
    if (currentSrc) onResolvedRef.current?.(currentSrc);
  }, [currentSrc]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  // A display-sized copy that failed to load; the original is drawn instead.
  const [failedSized, setFailedSized] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  /**
   * Live lookup: by ISBN ONLY.
   *
   * This used to fall back to an Open Library TITLE search and take the first
   * hit's cover_i -- a WORK's cover, which can belong to any edition in any
   * language. That is how The Unteleported Man showed "Mensonges & Cie", the
   * French Robert Laffont edition, to every visitor. An ISBN names one exact
   * edition, so it cannot be the wrong book. Anything the ISBN cannot answer is
   * left to the nightly validate-book-covers job, which only stores covers that
   * pass supabase/functions/_shared/bookCover.ts (English edition, title and
   * author must match, image gate); until then the placeholder shows.
   */
  const getOpenLibraryCover = async (_title: string, _author?: string, isbn?: string): Promise<string | null> => {
    if (!isbn) return null;
    const url = `https://covers.openlibrary.org/b/isbn/${encodeURIComponent(isbn)}-L.jpg?default=false`;
    try {
      await imageService.loadImage({ src: url, timeout: 5000 });
      return url;
    } catch {
      return null;
    }
  };

  /**
   * getArchiveCover was REMOVED on 2026-08-16.
   *
   * It derived a "cover" from an Archive.org item id — services/img/<id>,
   * <id>.jpg, __ia_thumb.jpg — every one of which is page ONE of the scan
   * rather than the cover. For an Early Bird Books edition page one is their
   * advert, so "The Cosmic Puppets" rendered a QR-code promo as its cover, and
   * cacheImageUrl then persisted it.
   *
   * Measured before removing, rather than assumed: 0 stored cover_url values in
   * author_books, transmissions or publisher_books point at archive.org — it was
   * never a curated source. All 69 archive.org covers in ebook_search_cache were
   * artefacts of this chain, and those rows were purged in the same change.
   *
   * It was also the most expensive step by a distance: a Supabase query against
   * free_ebook_links (485 rows carry an archive_url, so it usually hit) followed
   * by up to four sequential image loads. The Author Matrix renders 35 book
   * cards, each of which ran this — the bulk of the slow load reported.
   *
   * A missing cover now falls to PlaceholderCover, which is at least honest.
   */

  // The shared cover cache keyed `cover_<title>` was REMOVED on 2026-09-27. It
  // was keyed on the title alone (author stored as 'Unknown'), shared by every
  // visitor, never expired, and stored whatever the title search guessed -- so
  // one bad guess became the permanent cover for every book with that title.

  // Build robust fallbacks for Google Books image URLs
  const createImageFallbacks = (urls: string[]): string[] => {
    const variants = new Set<string>();

    const ensureParam = (u: string, key: string, value: string) =>
      new RegExp(`[?&]${key}=`).test(u) ? u : `${u}${u.includes('?') ? '&' : '?'}${key}=${value}`;

    for (const raw of urls) {
      if (!raw) continue;

      // Prefer HTTPS variant and keep original too
      const https = raw.replace(/^http:\/\//, 'https://');
      variants.add(raw);
      variants.add(https);

      // Special handling for Google Books content endpoint
      if (/books\.google(?:usercontent)?\.com\/books\/content/.test(https)) {
        const base = ensureParam(ensureParam(https, 'img', '1'), 'printsec', 'frontcover');
        variants.add(base);

        // Try both hosts as some titles only resolve on one
        variants.add(base.replace('books.google.com', 'books.googleusercontent.com'));
        variants.add(base.replace('books.googleusercontent.com', 'books.google.com'));

        // Provide multiple zoom levels (do NOT force 0)
        for (const z of [1, 2, 3, 4]) {
          const withZoom = /[?&]zoom=/.test(base)
            ? base.replace(/([?&])zoom=\d+/, `$1zoom=${z}`)
            : `${base}${base.includes('?') ? '&' : '?'}zoom=${z}`;
          variants.add(withZoom);
          variants.add(withZoom.replace('books.google.com', 'books.googleusercontent.com'));
        }

        // Also try keeping/adding edge=curl (some covers require it)
        if (!/[?&]edge=/.test(base)) {
          variants.add(`${base}${base.includes('?') ? '&' : '?'}edge=curl`);
        }
      }

      // Apple Books occasionally refuses the 600px artwork URL returned by the
      // search result while serving the same, already-validated edition at its
      // larger artwork size. The lightbox asks for this form and succeeds; keep
      // the inline preview on the same source before conceding to a placeholder.
      // This changes only image sizing — never the title, edition or provider.
      if (https.includes('mzstatic.com')) {
        const full = appleArtwork(https, 1200);
        if (full) variants.add(full);
      }
    }

    return Array.from(variants);
  };

  // Normalize http:// to https:// for known safe providers to prevent mixed-content blocks
  const normalizeProtocol = (url: string | undefined): string | undefined => {
    if (!url) return url;
    if (url.startsWith('http://')) {
      // Only upgrade known safe providers
      if (
        url.includes('books.google.com') ||
        url.includes('googleusercontent.com') ||
        url.includes('openlibrary.org') ||
        url.includes('covers.openlibrary.org') ||
        url.includes('image.tmdb.org') ||
        url.includes('archive.org')
      ) {
        return url.replace(/^http:\/\//, 'https://');
      }
    }
    return url;
  };

  useEffect(() => {
    // IMMEDIATE RENDER: If we have any cover URL, display it right away
    // Normalize protocol to prevent mixed-content blocks
    const firstAvailableUrl = normalizeProtocol(coverUrl) || normalizeProtocol(thumbnailUrl) || normalizeProtocol(smallThumbnailUrl);
    
    if (firstAvailableUrl) {
      setCurrentSrc(firstAvailableUrl);
      setIsLoading(false);
      // A cover that arrives LATE (the preview modal's Apple lookup lands
      // after this component's own lookup gave up) must replace the
      // placeholder. hasError was never cleared, so the card stayed blank while
      // the enlarge button -- which reads the same URL -- showed the cover.
      setHasError(false);

      // PREMIUM POLISH: a stored cover that is a low-res Archive.org thumbnail
      // (often a faint title-page scan) is upgraded to a crisp, high-res Open
      // Library cover when an ISBN is available. An ISBN match is the SAME book,
      // so there is no wrong-cover risk. Non-blocking, and it can only swap
      // upward — imageService rejects missing/placeholder images, so a miss
      // keeps the original cover.
      if (isbn && /archive\.org\/(?:services\/img|download)/.test(firstAvailableUrl)) {
        let cancelled = false;
        const hiRes = `https://covers.openlibrary.org/b/isbn/${encodeURIComponent(isbn)}-L.jpg?default=false`;
        imageService
          .loadImage({ src: hiRes, timeout: 6000 })
          .then(() => {
            if (!cancelled) setCurrentSrc(hiRes);
          })
          .catch(() => {
            /* no better cover available — keep the original */
          });
        return () => {
          cancelled = true;
        };
      }
      return;
    }

    // NO URL PROVIDED: look up the exact edition by ISBN.
    // `settled` doubles as cancellation: a lookup still in flight when the props
    // change (e.g. a real cover arrives) must not overwrite the newer state.
    let settled = false;
    const tryRecover = async () => {
      setIsLoading(true);
      setHasError(false);

      // Exact edition by ISBN, or nothing (see getOpenLibraryCover).
      const olCoverUrl = await getOpenLibraryCover(title, author, isbn);
      if (settled) return;
      if (olCoverUrl) {
        setCurrentSrc(olCoverUrl);
        setIsLoading(false);
        return;
      }

      // Nothing worked
      setIsLoading(false);
      setHasError(true);
    };

    /**
     * The spinner must always terminate, and before this it could not.
     *
     * tryRecover awaits getCachedImageUrl, getOpenLibraryCover and
     * getArchiveCover directly — no try/catch and no timeout on any of the
     * three. Only the imageService.loadImage calls inside them are guarded. So
     * if any lookup THREW, the promise rejected and the final
     * setIsLoading(false) below it never ran; if any lookup HUNG (getArchiveCover
     * goes out to an edge function), it never ran either. Either way the cover
     * sat on a loading skeleton indefinitely — observed on Storm and Names on
     * the Land, still spinning after a minute, while Earth Abides was fine
     * because it has a stored cover and never enters recovery at all.
     *
     * catch handles the throw, the watchdog handles the hang, and finally
     * guarantees the flag clears on the happy path too.
     */
    const watchdog = setTimeout(() => {
      if (settled) return;
      console.warn('Cover recovery timed out, showing placeholder:', title);
      setIsLoading(false);
      setHasError(true);
    }, COVER_RECOVERY_TIMEOUT_MS);

    tryRecover()
      .catch((err) => {
        if (settled) return;
        console.warn('Cover recovery failed:', title, err);
        setHasError(true);
      })
      .finally(() => {
        if (settled) return;
        settled = true;
        clearTimeout(watchdog);
        setIsLoading(false);
      });

    return () => {
      settled = true;
      clearTimeout(watchdog);
    };
  }, [coverUrl, thumbnailUrl, smallThumbnailUrl, title, author, isbn]);

  if (isLoading) {
    return (
      <div className={`${className} flex-shrink-0 rounded overflow-hidden bg-muted/30`}>
        <div className="w-full h-full animate-pulse flex items-center justify-center">
          <div className="w-4 h-4 border-2 border-muted-foreground/40 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  if (hasError || !currentSrc) {
    return <PlaceholderCover title={title} className={className} />;
  }

  // Load the cover at the size it is drawn (2x for retina), not the stored
  // 1200px original: a 64px grid cover was ~175 KB, now ~13 KB. A class
  // without a fixed h-N (h-full) keeps the original. If the sized copy fails,
  // the recovery below tries the original first.
  const cssHeight = Number(className.match(/(?:^|\s)h-(\d+)(?:\s|$)/)?.[1]) * 4;
  const sized = cssHeight ? coverAtSize(currentSrc, cssHeight * 2) : undefined;
  const displaySrc = sized && sized !== failedSized ? sized : currentSrc;

  return (
    <div className={`${className} flex-shrink-0 rounded overflow-hidden bg-muted/20`}>
      <OptimizedImage
        key={displaySrc}
        src={displaySrc}
        alt={title}
        width={parseInt(className.match(/w-(\d+)/)?.[1] || '12') * 4}
        height={parseInt(className.match(/h-(\d+)/)?.[1] || '16') * 4}
        fallbackSrc={getPlaceholderImage(title)}
        className="w-full h-full object-cover transition-all duration-300"
        lazy={lazy}
        priority={!lazy}
        onError={() => {
          if (displaySrc !== currentSrc) {
            setFailedSized(displaySrc);
            return;
          }
          setIsLoading(true);
          setHasError(false);

          const tryRecover = async () => {
            // Variants of every URL we were given (the sized copy was already
            // retried at full size above).
            const originalUrls = [coverUrl, thumbnailUrl, smallThumbnailUrl].filter(Boolean) as string[];
            const allUrls = createImageFallbacks(originalUrls);

            for (const url of allUrls) {
              if (url === displaySrc) continue; // the one that just failed
              try {
                await imageService.loadImage({ src: url, timeout: 5000 });
                setCurrentSrc(url);
                setIsLoading(false);
                return;
              } catch (error) {
                continue;
              }
            }

            // Exact edition by ISBN, or nothing. The title-keyed cache and the
            // title search are gone from BOTH chains (see getOpenLibraryCover).
            const olCoverUrl = await getOpenLibraryCover(title, author, isbn);
            if (olCoverUrl && olCoverUrl !== currentSrc) {
              setCurrentSrc(olCoverUrl);
              setIsLoading(false);
              return;
            }

            // Try Archive.org as last resort
            // Archive.org step removed here too — see the note where
            // getArchiveCover was. Both chains must stay in step.

            // All recovery attempts failed
            setIsLoading(false);
            setHasError(true);
          };

          // Same guard as the mount-time chain above: this block awaits the
          // same three unguarded lookups, so it can hang or reject the same way.
          // Kept in sync deliberately — if you change one, change both.
          let settled = false;
          const watchdog = setTimeout(() => {
            if (settled) return;
            console.warn('Cover re-recovery timed out, showing placeholder:', title);
            setIsLoading(false);
            setHasError(true);
          }, COVER_RECOVERY_TIMEOUT_MS);

          tryRecover()
            .catch((err) => {
              console.warn('Cover re-recovery failed:', title, err);
              setHasError(true);
            })
            .finally(() => {
              settled = true;
              clearTimeout(watchdog);
              setIsLoading(false);
            });
        }}
      />
    </div>
  );
};

const getPlaceholderImage = (title: string) => {
  const titleLower = title.toLowerCase();
  if (titleLower.includes('sci-fi') || titleLower.includes('science fiction') || 
      titleLower.includes('space') || titleLower.includes('robot') || 
      titleLower.includes('alien') || titleLower.includes('future') ||
      titleLower.includes('mars') || titleLower.includes('star') ||
      titleLower.includes('cyber') || titleLower.includes('android')) {
    return scifiPlaceholder;
  }
  if (titleLower.includes('classic') || titleLower.includes('vintage') ||
      titleLower.includes('victorian') || titleLower.includes('19th') ||
      titleLower.includes('18th') || titleLower.includes('century')) {
    return classicPlaceholder;
  }
  return generalPlaceholder;
};

const PlaceholderCover = ({ title, className = "w-12 h-16" }: { title: string; className?: string }) => {
  return (
    <div className={`${className} flex-shrink-0 rounded overflow-hidden relative bg-muted/30`}>
      <OptimizedImage
        src={getPlaceholderImage(title)}
        alt={`Placeholder cover for ${title}`}
        className="w-full h-full object-cover opacity-40"
        priority
      />
      <div className="absolute inset-0 bg-background/20 flex items-center justify-center">
        <div className="text-center p-2">
          <BookOpen className="w-4 h-4 text-muted-foreground mx-auto mb-1" />
          <div className="text-[8px] text-foreground/80 font-medium leading-tight">
            <span className="break-words line-clamp-2" style={{ wordBreak: 'break-word' }}>
              {title}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedBookCover;
